import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  const { t } = useLanguage();

  // All searchable content
  const searchableContent = [
    // Projects
    { title: 'Yamai Cardio', path: '/projects/cardio', type: 'project', keywords: 'heart ecg cardiovascular diagnostic' },
    { title: 'Yamai Neuro', path: '/projects/neuro', type: 'project', keywords: 'brain neural neurological eeg mri' },
    { title: 'Yamai Derma', path: '/projects/derma', type: 'project', keywords: 'skin dermatology computer vision' },
    { title: 'Yamai Pulmo', path: '/projects/pulmo', type: 'project', keywords: 'lung respiratory pulmonary xray' },
    { title: 'Yamai Vision', path: '/projects/vision', type: 'project', keywords: 'eye retinal ophthalmology' },
    { title: 'Yamai NutriScan', path: '/projects/nutriscan', type: 'project', keywords: 'nutrition metabolic digestive' },
    { title: 'Yamai HealthNet', path: '/projects/healthnet', type: 'project', keywords: 'unified ecosystem integration' },
    // Pages
    { title: t('nav.home'), path: '/', type: 'page', keywords: 'home main landing' },
    { title: t('nav.about'), path: '/about', type: 'page', keywords: 'about team mission vision' },
    { title: t('nav.projects'), path: '/projects', type: 'page', keywords: 'projects portfolio work' },
    { title: t('nav.contact'), path: '/contact', type: 'page', keywords: 'contact email form message' },
    { title: t('nav.faq'), path: '/faq', type: 'page', keywords: 'faq questions answers help' },
    { title: t('nav.privacy'), path: '/privacy', type: 'page', keywords: 'privacy policy gdpr data' },
  ];

  useEffect(() => {
    if (query.trim().length > 1) {
      const filtered = searchableContent.filter(item =>
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        item.keywords.toLowerCase().includes(query.toLowerCase())
      );
      setResults(filtered);
    } else {
      setResults([]);
    }
  }, [query]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleResultClick = (path) => {
    navigate(path);
    onClose();
    setQuery('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-[#1F2A3A] rounded-2xl border border-white/10 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300">
        {/* Search Input */}
        <div className="p-6 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-yamai-cyan-400 w-5 h-5" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects, pages..."
              className="w-full pl-12 pr-12 py-4 bg-[#0B1220] rounded-xl border border-white/10 focus:border-yamai-cyan-400 focus:outline-none text-white placeholder-gray-500 text-lg"
              autoFocus
            />
            <button
              onClick={onClose}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto p-4">
          {query.trim().length > 1 && results.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400">No results found for "{query}"</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-2">
              {results.map((result, index) => (
                <button
                  key={index}
                  onClick={() => handleResultClick(result.path)}
                  className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-yamai-cyan-400/50 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yamai-cyan-500/20 to-yamai-purple-500/20 flex items-center justify-center">
                      <Search className="w-5 h-5 text-yamai-cyan-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-semibold">{result.title}</p>
                      <p className="text-sm text-gray-400 capitalize">{result.type}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-yamai-cyan-400 group-hover:translate-x-1 transition-all" />
                </button>
              ))}
            </div>
          )}

          {query.trim().length <= 1 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Start typing to search...</p>
            </div>
          )}
        </div>

        {/* Footer Hint */}
        <div className="p-4 border-t border-white/10 bg-[#0B1220]/50">
          <p className="text-sm text-gray-500 text-center">
            Press <kbd className="px-2 py-1 bg-white/10 rounded">ESC</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
